import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { ITemplate } from './RunRiskAssessment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { AppPrimaryButton } from 'shared/components/AppComponents';
import { useEffect, useMemo, useState } from 'react';
import { RiskAssessmentService } from 'services/RiskAssessmentService';
import { Toast } from 'shared/helpers';
import DateFnsAdapter from '@date-io/date-fns';
import DoneIcon from '@mui/icons-material/Done';
import { useNavigate } from 'react-router-dom';
import { BaseApplicationState } from 'state/baseapplication/Reducer';
import { useSelector } from 'react-redux';

const dateFns = new DateFnsAdapter();

interface IAssessmentOverview {
  _id: string;
  templateId: string;
  aiInventoryId: string;
  datasetId: any;
  assessorId: string;
  assessorName: string;
  assessmentDate: string;
  status: string;
  completionDate: any;
  version: string;
  entityType: 'AI System' | 'Dataset';
  tenantId: string;
  isActive: boolean;
  deletedAt: any;
  createdBy: any;
  updatedBy: any;
  createdDate: string;
  updatedDate: any;
  assessmentType: string;
  template: ITemplate[];
  responses: { [key: string]: any };
  toUserEmail: string;
  toUserName: string;
}
interface AssessmentDetailOverviewProps {
  open: boolean;
  onCloseDialog: (status?: boolean) => void;
  assessmentId: string;
}
const CompletedAssessmentOverview: React.FC<AssessmentDetailOverviewProps> = (
  props
) => {
  const { open, onCloseDialog, assessmentId } = props;
  const [isLoadingAssessment, setIsLoadingAssessment] = useState(false);
  const [assessmentDetail, setAssessmentDetail] =
    useState<IAssessmentOverview>();
  const navigate = useNavigate();

  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );

  const templateDetail = useMemo(() => {
    if (assessmentDetail && assessmentDetail.template.length > 0)
      return assessmentDetail.template[0];
    return null;
  }, [assessmentDetail]);

  const userResponses = useMemo(
    () => assessmentDetail?.responses || {},
    [assessmentDetail]
  );
  const completedQuestions = useMemo(() => {
    if (userResponses)
      return Object.keys(userResponses).reduce((count, key) => {
        if (userResponses[key] !== '') {
          return count + 1;
        }
        return count;
      }, 0);
    return 0;
  }, [userResponses]);

  // API Calls
  const loadAssessmentDetail = (): void => {
    setIsLoadingAssessment(true);
    RiskAssessmentService.getAssessmentDetail(assessmentId)
      .then((response) => {
        const { data } = response.data;
        console.log('--------------prod', data);
        if (data.length > 0 && data[0].template) {
          setAssessmentDetail(data[0]);
        } else {
          Toast.showInfoMessage('Assessment details not found');
          onCloseDialog();
        }
      })
      .catch((error: any) => {
        Toast.showInfoMessage(error.message);
        onCloseDialog();
      })
      .finally(() => {
        setIsLoadingAssessment(false);
      });
  };

  // Instance Methods

  // UI Actions
  const actionContinueAssessment = (): void => {
    navigate(`/risk-assessments/${assessmentId}/continue`, {
      state: {
        assessment: {
          datasets: assessmentDetail?.datasetId
            ? [
                {
                  _id: assessmentDetail?.datasetId,
                  title: assessmentDetail?.datasetId,
                },
              ]
            : [],
          aiSystems: assessmentDetail?.aiInventoryId
            ? [
                {
                  _id: assessmentDetail?.aiInventoryId,
                  title: assessmentDetail?.aiInventoryId,
                },
              ]
            : [],
          assessmentIds: [assessmentDetail],
          template: templateDetail,
          userResponses,
        },
      },
    });
  };

  // Hooks
  useEffect(() => {
    if (assessmentId) {
      loadAssessmentDetail();
    }
  }, [assessmentId]);

  // UI Elements
  return (
    <Dialog maxWidth="md" fullWidth open={open}>
      {isLoadingAssessment && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}
      {!isLoadingAssessment && templateDetail && (
        <>
          <DialogTitle>
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
            >
              <Stack>
                <Typography variant="h3">
                  {templateDetail.templateName}
                </Typography>
                <Typography variant="body1">
                  View assessment details and responses
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center">
                <Chip
                  avatar={
                    assessmentDetail?.status === 'Completed' ? (
                      <DoneIcon />
                    ) : (
                      <AccessTimeIcon />
                    )
                  }
                  label={assessmentDetail?.status}
                  size="small"
                  color={
                    assessmentDetail?.status === 'Completed'
                      ? 'primary'
                      : 'default'
                  }
                />
                <IconButton onClick={() => onCloseDialog(false)}>
                  <HighlightOffIcon />
                </IconButton>
              </Stack>
            </Stack>
          </DialogTitle>
          <Divider />
          <DialogTitle>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              {assessmentDetail && assessmentDetail.assessmentType === 'self' && (
                <Stack
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="center"
                >
                  <PersonOutlineIcon className="mr-1" />
                  <Stack>
                    <Typography variant="body2">Conducted by</Typography>
                    <Typography variant="body1" className="fw-5">
                      {assessmentDetail?.assessorName}
                    </Typography>
                  </Stack>
                </Stack>
              )}
              <Stack direction="row" alignItems="center">
                <CalendarMonthIcon className="mr-1" />
                <Stack>
                  <Typography variant="body2">Assessment date</Typography>
                  <Typography variant="body1" className="fw-5">
                    {dateFns
                      .date(assessmentDetail?.assessmentDate)
                      .toDateString()}
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction="row" alignItems="center">
                <DescriptionIcon className="mr-1" />
                <Stack>
                  <Typography variant="body2">Template Version</Typography>
                  <Typography variant="body1" className="fw-5">
                    {templateDetail.version}
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction="row" alignItems="center">
                <CheckCircleOutlineIcon className="mr-1" />
                <Stack>
                  <Typography variant="body2">Progress</Typography>
                  <Typography variant="body1" className="fw-5">
                    {completedQuestions}/{templateDetail.noOfQuestions}{' '}
                    (Questions/Sections)
                  </Typography>
                </Stack>
              </Stack>
              {assessmentDetail && assessmentDetail.assessmentType !== 'self' && (
                <Stack
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="center"
                >
                  <PersonOutlineIcon className="mr-1" />
                  <Stack>
                    <Typography variant="body2">Recipient</Typography>
                    <Typography variant="body1" className="fw-5">
                      {assessmentDetail?.toUserName}
                      <br />
                      {assessmentDetail?.toUserEmail}
                    </Typography>
                  </Stack>
                </Stack>
              )}
            </Stack>
          </DialogTitle>
          <Divider />
          <DialogContent>
            {templateDetail.sections.map((section, sIndex) => (
              <Box key={section.title}>
                <Stack direction="row" alignItems="center">
                  <Chip label={`Section ${sIndex + 1}`} />
                  <Typography className="ml-2 fw-6 f18">
                    {section.title}
                  </Typography>
                </Stack>
                {section.subsections.map((sSection) => (
                  <Box className="ml-4" key={sSection.title}>
                    <Typography variant="h6" className="fw-6 my-4">
                      {sSection.title}
                    </Typography>
                    {sSection.questions.map((question, qIndex) => (
                      <Stack
                        direction="row"
                        alignItems="flex-start"
                        key={question.id}
                        style={{
                          border: '1px dashed lightgray',
                          borderRadius: '10px',
                          padding: 10,
                          margin: '5px 0px',
                        }}
                      >
                        <Chip
                          size="small"
                          className="mr-2"
                          label={`Q ${qIndex + 1}`}
                        />
                        <Box>
                          <Typography className="fw-5">
                            {question.text}
                          </Typography>
                          {/* {userResponses[question.id] && (
                              <Typography className="mt-2 fw-6">
                                {userResponses[question.id]}
                              </Typography>
                            )} */}
                          {userResponses[question.id] && (
                            <Typography className="mt-2 fw-6">
                              {question.type === 'SelectMany' &&
                                Array.isArray(userResponses[question.id]) &&
                                userResponses[question.id].join(', ')}
                              {!(
                                question.type === 'SelectMany' &&
                                Array.isArray(userResponses[question.id])
                              ) && userResponses[question.id]}
                            </Typography>
                          )}
                          {!userResponses[question.id] && (
                            <Typography className="mt-2">
                              <i>No response provided</i>
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    ))}
                  </Box>
                ))}
              </Box>
            ))}
          </DialogContent>
          {assessmentDetail?.status === 'In Progress' && (
            <DialogActions sx={{ justifyContent: 'space-between', px: 5 }}>
              <Typography style={{ color: 'gray' }}>
                {templateDetail.noOfQuestions - completedQuestions} Questions
                Pending
              </Typography>
              {orgACLConfig.riskManagement?.includes(
                'riskManagement.riskAssessments.continue'
              ) && (
                <AppPrimaryButton
                  onClick={actionContinueAssessment}
                  className="btn-small"
                  title="Continue Assessment"
                />
              )}
            </DialogActions>
          )}
        </>
      )}
    </Dialog>
  );
};

export default CompletedAssessmentOverview;
